import { FormsAnswerFragment } from 'generated/resources/graphql'

import { QuestionType, QuestionnaireType } from 'modules/forms/components/newFormEditor/types'
import { elementFromData } from 'modules/forms/hooks/useForms'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { ElementOption } from 'modules/forms/lib/types'
import { FormElement, QuizBlock } from 'modules/trainings/lib/content/QuizBlock'

export const mapOptions = (options: ElementOption[] | undefined): ElementOption[] => {
  if (!options) return []
  return options.map((opt) => ({
    id: opt.id,
    value: opt.value,
    label: opt.label,
    isCorrect: opt.isCorrect,
  }))
}

export const mapOptionsToSave = (options: ElementOption[] | null | undefined): ElementOption[] => {
  if (!options) return []
  return options.map((opt) => ({
    id: opt.id,
    value: opt.value,
    label: opt.label,
    isCorrect: opt.isCorrect,
  }))
}

const isValidQuestionType = (type: string): type is QuestionType['type'] => {
  return ['select', 'multiselect'].includes(type)
}

const mapElementType = (type: string): QuestionType['type'] => {
  return isValidQuestionType(type) ? type : 'select'
}

/**
 * Converts from QuizBlock to QuestionnaireType format for the form coCreationForm
 */
export const quizBlockToQuestionnaire = (quiz: QuizBlock): QuestionnaireType => {
  const mapElementToQuestionnaire = (element: FormElement) => ({
    id: element.id || undefined,
    label: element.label,
    description: element.description || '',
    type: mapElementType(element.type),
    position: element.position,
    mandatory: element.mandatory,
    locked: false,
    hasCorrectAnswer: element.hasCorrectAnswer || false,
    params: {},
    options: mapOptions(element.options),
  })

  const elements: QuestionnaireType['elements'] = quiz.form.elements.map((element) => {
    const baseElement = mapElementToQuestionnaire(element)

    if (
      element.type === 'section' &&
      element.childrenElements &&
      element.childrenElements.length > 0
    ) {
      return {
        ...baseElement,
        type: 'section' as const,
        elements: element.childrenElements.map(mapElementToQuestionnaire),
      }
    }

    return baseElement
  })

  return {
    title: quiz.form.title,
    description: quiz.form.description || '',
    elements,
  }
}

/**
 * Maps a single question element for saving
 */
const mapQuestionElementToSave = (element: QuestionType) => ({
  id: element.id,
  label: element.label,
  description: element.description || null,
  type: element.type,
  position: element.position,
  mandatory: element.mandatory,
  hasCorrectAnswer: element.hasCorrectAnswer,
  options: mapOptionsToSave(element.options),
})

/**
 * Updates a QuizBlock from QuestionnaireType
 */
export const updateQuizBlockFromQuestionnaire = (
  quiz: QuizBlock,
  questionnaire: QuestionnaireType
): void => {
  quiz.form.title = questionnaire.title
  quiz.form.description = questionnaire.description
  quiz.form.elements = questionnaire.elements.map((element) => {
    if (element.type === 'section' && 'elements' in element) {
      return {
        id: element.id,
        label: element.label,
        description: element.description || null,
        type: 'section' as const,
        position: element.position,
        mandatory: element.mandatory,
        hasCorrectAnswer: element.hasCorrectAnswer,
        options: mapOptionsToSave(element.options),
        childrenElements: element.elements.map(mapQuestionElementToSave),
      }
    }

    return mapQuestionElementToSave(element)
  })
}

export const populateFormWithAnswers = (form: Form, answers: FormsAnswerFragment[]): Form => {
  answers.forEach((answer) => {
    const elementId = answer?.elementId || ''
    const element = form.getElement(elementId)

    if (element && answer) {
      element.answer = {
        answer: answer.answer || '',
        comment: answer.comment || undefined,
        options: answer.elementOptionIds || [],
        isCorrect: answer.isCorrect ?? undefined,
      }
    }
  })

  return form
}

export const extractFormFromQuizBlock = (quiz: QuizBlock): Form => {
  quiz.title = quiz.title || ''

  const configuration = {
    allowedElements: quiz.form.configuration?.allowedElements || [],
    retryable: quiz.form.configuration?.retryable ?? true,
    scorable: quiz.form.configuration?.scorable ?? true,
  }

  const formInstance = new Form(
    quiz.form.id || '',
    quiz.form.title,
    quiz.form.description || null,
    quiz.form.resourceType,
    quiz.form.resourceId || null,
    'quiz',
    'draft',
    false,
    [],
    {},
    configuration
  )

  const createElementParams = (elementData: FormElement, sectionId?: string) => ({
    id: elementData.id || undefined,
    label: elementData.label,
    description: elementData.description || '',
    mandatory: elementData.mandatory,
    options: mapOptions(elementData.options),
    params: {},
    position: elementData.position,
    sectionId,
    type: elementData.type,
    hasCorrectAnswer: elementData.hasCorrectAnswer || true,
    answer: {
      isCorrect: elementData.answer?.isCorrect || false,
      comment: elementData.answer?.comment || undefined,
      answer: elementData.answer?.answer || '',
      options: elementData.answer?.options?.map((opt) => opt.id || '') || undefined,
    },
  })

  quiz.form.elements.forEach((elementData) => {
    const element = elementFromData(createElementParams(elementData))

    if (element instanceof SectionElement && elementData.childrenElements?.length) {
      elementData.childrenElements.forEach((childData) => {
        const childElement = elementFromData(createElementParams(childData, element.id))
        element.addElement(childElement)
      })
    }

    formInstance.addElement(element)
  })

  return formInstance
}
